import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Brain, LogOut, User, LayoutDashboard, BookOpen, Shield, Menu, X } from 'lucide-react';
import NotificationBell from './NotificationBell';

const LIGHT_PATHS = ['/', '/login', '/register', '/blog', '/testler', '/bilimsel-dayanak', '/hakkimizda', '/iletisim'];

const Header = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const isLight = LIGHT_PATHS.some(p => p === location.pathname) || location.pathname.startsWith('/blog') || location.pathname.startsWith('/testler') || location.pathname.startsWith('/bilimsel') || location.pathname.startsWith('/hakkimizda') || location.pathname.startsWith('/iletisim');
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 32);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  const handleLogout = async () => { await logout(); navigate('/'); };
  const initial = user?.name?.charAt(0)?.toLocaleUpperCase('tr-TR') || 'U';

  /* ─── FLOATING LIGHT THEME (Public pages) ─── */
  if (isLight && !user) {
    return (
      <div style={{ position: 'sticky', top: 16, zIndex: 100, padding: '0 24px', pointerEvents: 'none' }} data-testid="header-float-wrapper">
        <header
          style={{
            maxWidth: 1320,
            margin: '0 auto',
            height: 60,
            borderRadius: 18,
            background: scrolled ? 'rgba(17,28,42,0.82)' : 'rgba(17,28,42,0.62)',
            backdropFilter: 'blur(14px)',
            WebkitBackdropFilter: 'blur(14px)',
            border: `1px solid ${scrolled ? 'rgba(255,255,255,0.10)' : 'rgba(255,255,255,0.06)'}`,
            boxShadow: scrolled ? '0 8px 32px rgba(0,0,0,0.18)' : '0 4px 20px rgba(0,0,0,0.10)',
            transition: 'background 0.35s, border-color 0.35s, box-shadow 0.35s',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '0 28px',
            pointerEvents: 'auto',
          }}
          data-testid="header"
        >
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2.5" style={{ textDecoration: 'none' }} data-testid="header-logo">
            <img src="/logo/icon-dark.svg" alt="" style={{ height: 28, width: 28 }} />
            <span style={{ fontSize: 17, fontWeight: 700, color: '#F6F7F8', letterSpacing: '-0.01em' }}>
              Doktor<span style={{ color: '#D9A258' }}>Cebinde</span>
            </span>
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden md:flex items-center" style={{ gap: 4 }}>
            <Link to="/testler" style={{ fontSize: 13, fontWeight: 500, color: 'rgba(246,247,248,0.55)', textDecoration: 'none', padding: '6px 12px', borderRadius: 10, transition: 'color 0.2s' }}
              onMouseEnter={e => e.currentTarget.style.color = 'rgba(246,247,248,0.9)'}
              onMouseLeave={e => e.currentTarget.style.color = 'rgba(246,247,248,0.55)'}
              data-testid="header-testler-link">Testler</Link>
            <Link to="/bilimsel-dayanak" style={{ fontSize: 13, fontWeight: 500, color: 'rgba(246,247,248,0.55)', textDecoration: 'none', padding: '6px 12px', borderRadius: 10, transition: 'color 0.2s' }}
              onMouseEnter={e => e.currentTarget.style.color = 'rgba(246,247,248,0.9)'}
              onMouseLeave={e => e.currentTarget.style.color = 'rgba(246,247,248,0.55)'}
              data-testid="header-bilimsel-link">Bilimsel Dayanak</Link>
            <Link to="/hakkimizda" style={{ fontSize: 13, fontWeight: 500, color: 'rgba(246,247,248,0.55)', textDecoration: 'none', padding: '6px 12px', borderRadius: 10, transition: 'color 0.2s' }}
              onMouseEnter={e => e.currentTarget.style.color = 'rgba(246,247,248,0.9)'}
              onMouseLeave={e => e.currentTarget.style.color = 'rgba(246,247,248,0.55)'}
              data-testid="header-hakkimizda-link">Hakkımızda</Link>
            <Link to="/blog" style={{ fontSize: 13, fontWeight: 500, color: 'rgba(246,247,248,0.55)', textDecoration: 'none', padding: '6px 12px', borderRadius: 10, transition: 'color 0.2s' }}
              onMouseEnter={e => e.currentTarget.style.color = 'rgba(246,247,248,0.9)'}
              onMouseLeave={e => e.currentTarget.style.color = 'rgba(246,247,248,0.55)'}
              data-testid="header-blog-link-public">Blog</Link>
            <Link to="/iletisim" style={{ fontSize: 13, fontWeight: 500, color: 'rgba(246,247,248,0.55)', textDecoration: 'none', padding: '6px 12px', borderRadius: 10, transition: 'color 0.2s' }}
              onMouseEnter={e => e.currentTarget.style.color = 'rgba(246,247,248,0.9)'}
              onMouseLeave={e => e.currentTarget.style.color = 'rgba(246,247,248,0.55)'}
              data-testid="header-iletisim-link">İletişim</Link>
          </nav>

          {/* Desktop Actions */}
          <div className="hidden md:flex items-center" style={{ gap: 10 }}>
            <Link to="/login" style={{ fontSize: 14, fontWeight: 500, color: 'rgba(246,247,248,0.6)', textDecoration: 'none', padding: '6px 12px', transition: 'color 0.2s' }}
              onMouseEnter={e => e.currentTarget.style.color = '#F6F7F8'}
              onMouseLeave={e => e.currentTarget.style.color = 'rgba(246,247,248,0.6)'}
              data-testid="header-login-link">Giriş Yap</Link>
            <Link to="/register" style={{
              fontSize: 14, fontWeight: 600, color: '#1A2B3C',
              background: 'rgba(246,247,248,0.88)', borderRadius: 4,
              padding: '8px 18px', textDecoration: 'none',
              transition: 'all 0.2s',
            }}
              onMouseEnter={e => { e.currentTarget.style.background = '#F6F7F8'; }}
              onMouseLeave={e => { e.currentTarget.style.background = 'rgba(246,247,248,0.88)'; }}
              data-testid="header-register-link">Kayıt Ol</Link>
          </div>

          {/* Mobile Hamburger */}
          <button
            className="md:hidden flex items-center justify-center"
            style={{ width: 36, height: 36, borderRadius: 10, background: 'rgba(255,255,255,0.08)', border: 'none', cursor: 'pointer', color: 'rgba(246,247,248,0.7)' }}
            onClick={() => setMobileOpen(!mobileOpen)}
            data-testid="header-mobile-toggle"
          >
            {mobileOpen ? <X size={18} /> : <Menu size={18} />}
          </button>
        </header>

        {/* Mobile Dropdown */}
        {mobileOpen && (
          <div style={{
            maxWidth: 1320, margin: '8px auto 0', borderRadius: 14,
            background: 'rgba(17,28,42,0.92)', backdropFilter: 'blur(14px)',
            border: '1px solid rgba(255,255,255,0.08)',
            boxShadow: '0 12px 40px rgba(0,0,0,0.2)',
            padding: '16px 20px', pointerEvents: 'auto',
            display: 'flex', flexDirection: 'column', gap: 4,
          }} data-testid="header-mobile-menu">
            <Link to="/testler" style={{ fontSize: 15, fontWeight: 500, color: 'rgba(246,247,248,0.7)', textDecoration: 'none', padding: '10px 12px', borderRadius: 10 }} data-testid="header-mobile-testler">Testler</Link>
            <Link to="/bilimsel-dayanak" style={{ fontSize: 15, fontWeight: 500, color: 'rgba(246,247,248,0.7)', textDecoration: 'none', padding: '10px 12px', borderRadius: 10 }} data-testid="header-mobile-bilimsel">Bilimsel Dayanak</Link>
            <Link to="/hakkimizda" style={{ fontSize: 15, fontWeight: 500, color: 'rgba(246,247,248,0.7)', textDecoration: 'none', padding: '10px 12px', borderRadius: 10 }} data-testid="header-mobile-hakkimizda">Hakkımızda</Link>
            <Link to="/blog" style={{ fontSize: 15, fontWeight: 500, color: 'rgba(246,247,248,0.7)', textDecoration: 'none', padding: '10px 12px', borderRadius: 10 }} data-testid="header-mobile-blog">Blog</Link>
            <Link to="/iletisim" style={{ fontSize: 15, fontWeight: 500, color: 'rgba(246,247,248,0.7)', textDecoration: 'none', padding: '10px 12px', borderRadius: 10 }} data-testid="header-mobile-iletisim">İletişim</Link>
            <Link to="/login" style={{ fontSize: 15, fontWeight: 500, color: 'rgba(246,247,248,0.7)', textDecoration: 'none', padding: '10px 12px', borderRadius: 10 }} data-testid="header-mobile-login">Giriş Yap</Link>
            <Link to="/register" style={{ fontSize: 15, fontWeight: 600, color: '#1A2B3C', background: 'rgba(246,247,248,0.88)', textDecoration: 'none', padding: '10px 12px', borderRadius: 4, textAlign: 'center', marginTop: 4 }} data-testid="header-mobile-register">Kayıt Ol</Link>
          </div>
        )}
      </div>
    );
  }

  /* ─── DARK GLASS THEME (All authenticated pages — UNTOUCHED) ─── */
  return (
    <header className="sticky top-0 z-50" style={{
      background: 'rgba(10, 18, 38, 0.72)',
      backdropFilter: 'blur(16px)',
      WebkitBackdropFilter: 'blur(16px)',
      borderBottom: '1px solid rgba(120, 150, 220, 0.10)',
    }} data-testid="header">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
        <Link to={user ? '/dashboard' : '/'} className="flex items-center gap-2.5 group" data-testid="header-logo">
          <img src="/logo/icon-dark.svg" alt="" style={{ height: 28, width: 28 }} />
          <span style={{ fontSize: '18px', fontWeight: 700, color: 'rgba(255,255,255,0.92)' }}>
            Doktor<span style={{ color: '#D9A258' }}>Cebinde</span>
          </span>
        </Link>
        <nav className="flex items-center gap-0.5 sm:gap-1">
          {user ? (
            <>
              <Link to="/dashboard" className={`flex items-center gap-1.5 px-2 sm:px-3 py-2 rounded-xl text-xs sm:text-sm transition-all hover:bg-white/8 ${location.pathname === '/dashboard' ? 'bg-white/8' : ''}`}
                style={{ color: location.pathname === '/dashboard' ? 'rgba(255,255,255,0.95)' : 'rgba(220,230,255,0.72)', fontWeight: 500 }}
                data-testid="header-dashboard-link">
                <LayoutDashboard className="w-4 h-4 shrink-0" /> <span>Ana Sayfa</span>
              </Link>
              <Link to="/blog" className={`flex items-center gap-1.5 px-2 sm:px-3 py-2 rounded-xl text-xs sm:text-sm transition-all hover:bg-white/8 ${location.pathname.startsWith('/blog') ? 'bg-white/8' : ''}`}
                style={{ color: location.pathname.startsWith('/blog') ? 'rgba(255,255,255,0.95)' : 'rgba(220,230,255,0.72)', fontWeight: 500 }}
                data-testid="header-blog-link">
                <BookOpen className="w-4 h-4 shrink-0" /> <span>Blog</span>
              </Link>
              <Link to="/profile" className={`flex items-center gap-1.5 px-2 sm:px-3 py-2 rounded-xl text-xs sm:text-sm transition-all hover:bg-white/8 ${location.pathname === '/profile' ? 'bg-white/8' : ''}`}
                style={{ color: location.pathname === '/profile' ? 'rgba(255,255,255,0.95)' : 'rgba(220,230,255,0.72)', fontWeight: 500 }}
                data-testid="header-profile-link">
                <User className="w-4 h-4 shrink-0" /> <span>Profil</span>
              </Link>
              {user?.role === 'admin' && (
                <Link to="/admin" className={`flex items-center gap-1.5 px-2 sm:px-3 py-2 rounded-xl text-xs sm:text-sm transition-all hover:bg-purple-500/10 ${location.pathname === '/admin' ? 'bg-purple-500/10' : ''}`}
                  style={{ color: location.pathname === '/admin' ? '#C4B5FD' : 'rgba(167,139,250,0.7)', fontWeight: 500 }}
                  data-testid="header-admin-link">
                  <Shield className="w-4 h-4 shrink-0" /> <span className="hidden sm:inline">Admin</span>
                </Link>
              )}
              <button onClick={handleLogout} className="flex items-center gap-1.5 px-2 sm:px-3 py-2 rounded-xl text-xs sm:text-sm transition-all hover:bg-red-500/10"
                style={{ color: 'rgba(220,230,255,0.55)', fontWeight: 500 }}
                data-testid="header-logout-button">
                <LogOut className="w-4 h-4 shrink-0" /> <span className="hidden sm:inline">Çıkış</span>
              </button>
              <NotificationBell />
              <div className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold text-white ml-1" style={{
                background: 'linear-gradient(135deg, #3B82F6, #A78BFA)',
                boxShadow: '0 0 12px rgba(59,130,246,0.3)',
              }} data-testid="header-avatar">{initial}</div>
            </>
          ) : (
            <>
              <Link to="/blog" className="px-4 py-2 rounded-xl text-sm font-medium transition-all hover:bg-white/8" style={{ color: 'rgba(220,230,255,0.72)' }} data-testid="header-blog-link-public">Blog</Link>
              <Link to="/login" className="px-4 py-2 rounded-xl text-sm font-medium transition-all hover:bg-white/8" style={{ color: 'rgba(220,230,255,0.72)' }} data-testid="header-login-link">Giriş Yap</Link>
              <Link to="/register" className="px-4 py-2 rounded-xl text-sm font-medium text-white transition-all hover:-translate-y-0.5" style={{
                backgroundColor: '#3B82F6', boxShadow: '0 0 16px rgba(59,130,246,0.3)',
              }} data-testid="header-register-link">Kayıt Ol</Link>
            </>
          )}
        </nav>
      </div>
    </header>
  );
};

export default Header;
